/**
 * Google Ads / UTM first-party attribution helper.
 *
 * Behaviour:
 *  • On every page load, ensure we have a persistent `ak_ad_session` UUID
 *    in localStorage (rotates every 90 days for privacy).
 *  • If the current URL has `gclid` (Google Ads auto-tagging) OR any
 *    `utm_*` param, POST it to /api/ads/track-landing so the backend can
 *    upsert the landing row.
 *  • Provides `getAdSessionId()` so AuthCallback / Chat / Wallet flows
 *    can ship it along with their POSTs and the backend can stitch the
 *    funnel together.
 *
 * Privacy: We never read or send PII. The session_id is anonymous and
 * regenerated on a 90-day window.
 */
import api from "./api";

const STORAGE_KEY = "ak_ad_session";
const STORAGE_KEY_PARAMS = "ak_ad_params";   // store last seen utm/gclid
const STORAGE_KEY_LANDED = "ak_ad_landed_at";

const SESSION_TTL_DAYS = 90;

function uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "ak_" + Math.random().toString(36).slice(2, 14) + Date.now().toString(36);
}

export function getAdSessionId() {
  try {
    let id = localStorage.getItem(STORAGE_KEY);
    const landedAt = parseInt(localStorage.getItem(STORAGE_KEY_LANDED) || "0", 10);
    const expired = !landedAt || Date.now() - landedAt > SESSION_TTL_DAYS * 86400 * 1000;
    if (!id || expired) {
      id = uuid();
      localStorage.setItem(STORAGE_KEY, id);
      localStorage.setItem(STORAGE_KEY_LANDED, String(Date.now()));
    }
    return id;
  } catch (_) {
    return null;
  }
}

function readUrlParams() {
  if (typeof window === "undefined") return null;
  const sp = new URLSearchParams(window.location.search);
  const get = (k) => (sp.get(k) || "").trim() || null;
  return {
    gclid: get("gclid") || get("wbraid") || get("gbraid") || null,
    utm_source: get("utm_source"),
    utm_medium: get("utm_medium"),
    utm_campaign: get("utm_campaign"),
    utm_term: get("utm_term"),
    utm_content: get("utm_content"),
  };
}

/**
 * Fire-and-forget landing tracker. Call once per app boot from App.js.
 * Safe to call on every page (the backend upserts).
 */
export async function trackLanding() {
  if (typeof window === "undefined") return;
  const session_id = getAdSessionId();
  if (!session_id) return;
  const params = readUrlParams();
  // Persist gclid/utm if we just saw fresh ones — survives later page
  // navigations even after the URL is cleaned up.
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY_PARAMS) || "null");
    const fresh = Object.values(params).some((v) => !!v);
    const merged = fresh ? params : (stored || params);
    if (fresh) localStorage.setItem(STORAGE_KEY_PARAMS, JSON.stringify(params));

    // Only POST when we have SOMETHING actionable, otherwise we'd spam
    // the endpoint with empty rows on every page view.
    const isAdLike = !!(merged.gclid || (merged.utm_source || "").toLowerCase().includes("google"));
    const hasAnyParam = Object.values(merged).some((v) => !!v);
    if (!hasAnyParam && !fresh) return;

    await api.post("/ads/track-landing", {
      session_id,
      gclid: merged.gclid,
      utm_source: merged.utm_source,
      utm_medium: merged.utm_medium,
      utm_campaign: merged.utm_campaign,
      utm_term: merged.utm_term,
      utm_content: merged.utm_content,
      landing_path: window.location.pathname + window.location.search,
      referrer: document.referrer || null,
      _is_ad_hint: isAdLike,    // pure debug, backend ignores
    });
  } catch (_e) {
    // never throw from tracker
  }
}
